import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import MatchTable from "../../components/MatchTabel";
import { getLatestSeason, getMatchesForTeamBySeason, getUpcomingMatchesForTeam } from "../../services/MatchService";
import { Match } from "../../models/Match";
import Loading from "../../components/Loading";
import MatchTabs from "../../components/MatchTabs";
import { UpcomingMatch } from "../../models/UpcomingMatch";
import UpcomingMatchTable from "../../components/UpcomingMatchTable";
import { divisionMappings } from "../../Constants";
import { TournamentConfig } from "../../services/TournamentService";
import { getDivisionDisplayName } from "../../utils/divisionUtils";
import { useTranslation } from "react-i18next";
import { useLiveMatches } from "../../services/LiveService";
import LiveMatchGrid from "../LivePage/LiveMatchGrid";

interface TeamMatchesProps {
  season: number;
  tournamentConfigs?: TournamentConfig[];
  onDivisionsLoaded?: (divisions: string[]) => void;
  // Pass both to share the division filter with the parent (the team page's roster follows it);
  // without them the filter is local.
  divisionFilter?: string;
  onDivisionFilterChange?: (division: string) => void;
  /** The squad's canonical id; defaults to the URL's. */
  teamId?: string;
  /** Every OverPass id the squad plays under this season; live maps carry one of these. */
  registrationIds?: string[];
}

const TeamMatches = ({ season, tournamentConfigs, onDivisionsLoaded, divisionFilter: sharedFilter, onDivisionFilterChange, teamId: squadId, registrationIds }: TeamMatchesProps) => {
  const params = useParams<{ teamId: string }>();
  const teamId = squadId ?? params.teamId;
  const [activeTab, setActiveTab] = useState<string>('results');
  const [results, setResults] = useState<Match[]>([]);
  const [currentSeason, setCurrentSeason] = useState<number>(season);
  const [loading, setLoading] = useState(true);
  const [matchesByDate, setMatchesByDate] = useState<{ [date: string]: UpcomingMatch[] }>({});
  const [localFilter, setLocalFilter] = useState<string>('all');
  const divisionFilter = sharedFilter ?? localFilter;
  const setDivisionFilter = onDivisionFilterChange ?? setLocalFilter;

  const fetchUpcomingMatches = useCallback(async () => {
    if (!teamId) return;
    try {
      const newMatches = await getUpcomingMatchesForTeam(teamId);
      if (!newMatches || newMatches.length === 0) {
        return;
      }

      setMatchesByDate(prevMatchesByDate => {
        const updatedMatchesByDate = { ...prevMatchesByDate };
        newMatches.forEach(match => {
          const matchDate = match.match_time.split('T')[0]; // Extract date
          updatedMatchesByDate[matchDate] = updatedMatchesByDate[matchDate] || [];
          const existingMatchIndex = updatedMatchesByDate[matchDate].findIndex(m => m.challonge_id === match.challonge_id);
          if (existingMatchIndex === -1) {
            updatedMatchesByDate[matchDate].push(match);
          } else {
            // If the match already exists, update it
            updatedMatchesByDate[matchDate][existingMatchIndex] = match;
          }
        });
        return updatedMatchesByDate;
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [teamId]);

  const getNewestSeason = useCallback(async () => {
    try {
      const fetchedSeason = await getLatestSeason();
      setCurrentSeason(fetchedSeason);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const getMatches = useCallback(async () => {
    try {
      if (!teamId) throw new Error('No teamId provided');
      const matches = await getMatchesForTeamBySeason(teamId, season);
      setResults(matches);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [teamId, season]);

  useEffect(() => {
    fetchUpcomingMatches();
  }, [fetchUpcomingMatches]);

  useEffect(() => {
    getMatches();
  }, [getMatches]);

  useEffect(() => {
    getNewestSeason();
  }, [getNewestSeason]);

  // This team's maps that are over but not parsed yet. The live feed keeps their final
  // scoreboard until the demo is in; same cards as the Kampe page's Resultater.
  const { t } = useTranslation('pages');
  const { finished } = useLiveMatches(currentSeason === season);
  const justFinished = useMemo(() => {
    const parsed = new Set(results.map(m => m.matchid));
    const ours = new Set<string | null>(registrationIds ?? (teamId ? [teamId] : []));
    return finished.filter(m => (ours.has(m.team1.teamId) || ours.has(m.team2.teamId))
      && !parsed.has(m.matchId)
      && (divisionFilter === 'all' || m.division === divisionFilter));
  }, [finished, results, teamId, registrationIds, divisionFilter]);

  // Get all upcoming matches as a flat array
  const allUpcomingMatches = useMemo(() => {
    return Object.values(matchesByDate).flat();
  }, [matchesByDate]);

  // Extract unique divisions from both results and upcoming matches
  const availableDivisions = useMemo(() => {
    const divisions = new Set<string>();
    results.forEach(match => {
      if (match.division) {
        divisions.add(match.division);
      }
    });
    allUpcomingMatches.forEach(match => {
      if (match.division) {
        divisions.add(match.division);
      }
    });
    return Array.from(divisions).sort();
  }, [results, allUpcomingMatches]);

  // Extract unique divisions from results only (for results tab count)
  const resultsDivisions = useMemo(() => {
    const divisions = new Set<string>();
    results.forEach(match => {
      if (match.division) {
        divisions.add(match.division);
      }
    });
    return Array.from(divisions).sort();
  }, [results]);

  // Notify parent of loaded divisions (for header display)
  useEffect(() => {
    if (onDivisionsLoaded && availableDivisions.length > 0) {
      onDivisionsLoaded(availableDivisions);
    }
  }, [availableDivisions, onDivisionsLoaded]);

  // Extract unique divisions from upcoming matches only (for upcoming tab count)
  const upcomingDivisions = useMemo(() => {
    const divisions = new Set<string>();
    allUpcomingMatches.forEach(match => {
      if (match.division) {
        divisions.add(match.division);
      }
    });
    return Array.from(divisions).sort();
  }, [allUpcomingMatches]);

  // Show division filter tabs if multiple divisions OR if any kval/tournament division exists
  const hasKvalDivision = (divs: string[]) => divs.some(d => d.includes("kval") || !divisionMappings.has(d));
  const showResultsTabs = resultsDivisions.length > 1 || hasKvalDivision(resultsDivisions);
  const showUpcomingTabs = upcomingDivisions.length > 1 || hasKvalDivision(upcomingDivisions);
  const showAllTabs = availableDivisions.length > 1 || hasKvalDivision(availableDivisions);

  // Filter matches by selected division
  const filteredMatches = useMemo(() => {
    if (divisionFilter === 'all') {
      return results;
    }
    return results.filter(match => match.division === divisionFilter);
  }, [results, divisionFilter]);

  // Filter upcoming matches by selected division
  const filteredUpcomingMatchesByDate = useMemo(() => {
    if (divisionFilter === 'all') {
      return matchesByDate;
    }
    const filtered: { [date: string]: UpcomingMatch[] } = {};
    Object.entries(matchesByDate).forEach(([date, matches]) => {
      const filteredForDate = matches.filter(match => match.division === divisionFilter);
      if (filteredForDate.length > 0) {
        filtered[date] = filteredForDate;
      }
    });
    return filtered;
  }, [matchesByDate, divisionFilter]);

  // Get display name for division (resolves tournament display names)
  const getDisplayName = (division: string) => {
    if (division === 'all') return 'Alle';
    return getDivisionDisplayName(division, tournamentConfigs);
  };

  return (
    <div>
      {
        currentSeason === season ? (
          <>
            <MatchTabs activeTab={activeTab} onTabChange={setActiveTab} />
            <hr />
            {loading && <Loading />}

            {!loading && activeTab === 'upcoming' && (
              <div>
                {/* Division filter tabs for upcoming - only show if team plays in multiple divisions */}
                {showUpcomingTabs && (
                  <div className="d-flex justify-content-center mb-3 gap-2 flex-wrap">
                    <button
                      className={`btn btn-sm ${divisionFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setDivisionFilter('all')}
                    >
                      Alle ({allUpcomingMatches.length})
                    </button>
                    {upcomingDivisions.map(division => (
                      <button
                        key={division}
                        className={`btn btn-sm ${divisionFilter === division ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setDivisionFilter(division)}
                      >
                        {getDisplayName(division)} ({allUpcomingMatches.filter(m => m.division === division).length})
                      </button>
                    ))}
                  </div>
                )}
                {Object.entries(filteredUpcomingMatchesByDate).map(([date, matchesForDate], index) => (
                  <div className="text-center" key={index}>
                    <h2>{date}</h2>
                    {/*
                      Pass 'season' into UpcomingMatchTable
                      so it can link to /team/:teamId?season=...
                    */}
                    <UpcomingMatchTable matchesForDate={matchesForDate} season={season} showDivision={showUpcomingTabs && divisionFilter === 'all'} />
                  </div>
                ))}
              </div>
            )}

            {!loading && activeTab === 'results' && (
              <div>
                {/* Division filter tabs - only show if team plays in multiple divisions */}
                {showResultsTabs && (
                  <div className="d-flex justify-content-center mb-3 gap-2 flex-wrap">
                    <button
                      className={`btn btn-sm ${divisionFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setDivisionFilter('all')}
                    >
                      Alle ({results.length})
                    </button>
                    {resultsDivisions.map(division => (
                      <button
                        key={division}
                        className={`btn btn-sm ${divisionFilter === division ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setDivisionFilter(division)}
                      >
                        {getDisplayName(division)} ({results.filter(m => m.division === division).length})
                      </button>
                    ))}
                  </div>
                )}
                {justFinished.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-center mb-3">{t('live.justFinished')}</h4>
                    <LiveMatchGrid matches={justFinished} />
                  </div>
                )}
                {/*
                  Pass 'season' to MatchTable so it can
                  include ?season=... in links to teams
                */}
                <MatchTable matches={filteredMatches} compact={false} season={season} showDivision={showResultsTabs && divisionFilter === 'all'} />
              </div>
            )}
          </>
        ) : (
          <div>
            {loading ? <Loading /> : (
              <>
                {/* Division filter tabs - only show if team plays in multiple divisions */}
                {showAllTabs && (
                  <div className="d-flex justify-content-center mb-3 gap-2 flex-wrap">
                    <button
                      className={`btn btn-sm ${divisionFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setDivisionFilter('all')}
                    >
                      Alle ({results.length})
                    </button>
                    {availableDivisions.map(division => (
                      <button
                        key={division}
                        className={`btn btn-sm ${divisionFilter === division ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setDivisionFilter(division)}
                      >
                        {getDisplayName(division)} ({results.filter(m => m.division === division).length})
                      </button>
                    ))}
                  </div>
                )}
                {/* If currentSeason !== season, we still show results table */}
                <MatchTable matches={filteredMatches} compact={false} season={season} showDivision={showAllTabs && divisionFilter === 'all'} />
              </>
            )}
          </div>
        )
      }
    </div>
  );
};

export default TeamMatches;
